import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/layout/Navbar';
import Footer from '../../../components/layout/Footer';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Lock, CheckCircle, Clock, PlayCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLearnAuth } from '@/hooks/useLearnAuth';
import { canAccessModule, checkMasteryGate } from '@/utils/progressTracking';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { toast } from 'sonner';

interface Module {
  id: string;
  title: string;
  slug: string;
  level: string;
  module_order: number;
  duration_minutes: number;
}

interface ModuleWithStatus extends Module {
  status: 'completed' | 'available' | 'locked';
  progress?: any;
}

const LearnModules: React.FC = () => {
  const { user } = useLearnAuth();
  const [modules, setModules] = useState<ModuleWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [masteryGateStatus, setMasteryGateStatus] = useState<any>(null);

  useEffect(() => {
    if (!user) return;

    const fetchModules = async () => {
      try {
        // Fetch all modules
        const { data: modulesData, error: modulesError } = await supabase
          .from('modules')
          .select('*')
          .order('module_order');

        if (modulesError) throw modulesError;

        // Fetch user progress
        const { data: progressData } = await supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', user.id);

        const progressMap = new Map(progressData?.map(p => [p.module_id, p]) || []);

        // Check mastery gate
        const masteryStatus = await checkMasteryGate(user.id);
        setMasteryGateStatus(masteryStatus);

        // Determine status for each module
        const modulesWithStatus: ModuleWithStatus[] = await Promise.all(
          (modulesData || []).map(async (module) => {
            const progress = progressMap.get(module.id);
            const canAccess = await canAccessModule(user.id, module.module_order);

            let status: 'completed' | 'available' | 'locked';
            if (progress?.completed_at) {
              status = 'completed';
            } else if (canAccess) {
              status = 'available';
            } else {
              status = 'locked';
            }

            return {
              ...module,
              status,
              progress
            };
          })
        );

        setModules(modulesWithStatus);
      } catch (error) {
        console.error('Error fetching modules:', error);
        toast.error('Failed to load modules');
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const getLevelBadgeVariant = (level: string) => {
    switch (level) {
      case 'novice':
        return 'secondary';
      case 'practitioner':
        return 'default';
      case 'advanced':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusMessage = (module: ModuleWithStatus) => {
    if (module.status === 'locked') {
      if (module.module_order >= 9 && masteryGateStatus && !masteryGateStatus.canProgress) {
        return `Mastery Gate: ${masteryGateStatus.requiredQuizzesPassed - masteryGateStatus.quizzesPassed} more quizzes needed`;
      }
      return 'Complete previous modules to unlock';
    }
    return null;
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 text-brand-navy">My Modules</h1>
            <p className="text-gray-600">Complete modules in order to unlock the next one</p>
            
            {masteryGateStatus && modules.some(m => m.module_order >= 9) && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Mastery Gate (Modules 9-16)</h3>
                <p className="text-sm text-blue-800">
                  {masteryGateStatus.canProgress ? (
                    <>✅ Unlocked! Average score: {masteryGateStatus.averageScore.toFixed(1)}%</>
                  ) : (
                    <>
                      Pass {masteryGateStatus.requiredQuizzesPassed} quizzes with 80%+ to unlock advanced modules. 
                      Progress: {masteryGateStatus.quizzesPassed}/{masteryGateStatus.requiredQuizzesPassed} | 
                      Average: {masteryGateStatus.averageScore.toFixed(1)}%
                    </>
                  )}
                </p>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {modules.map((module) => (
              <Card 
                key={module.id} 
                className={`p-6 ${module.status === 'locked' ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">Module {module.module_order}</Badge>
                      <Badge variant={getLevelBadgeVariant(module.level)}>
                        {module.level}
                      </Badge>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">{module.title}</h3>
                  </div>
                  
                  {module.status === 'completed' && (
                    <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  )}
                  {module.status === 'locked' && (
                    <Lock className="w-8 h-8 text-gray-400 flex-shrink-0" />
                  )}
                  {module.status === 'available' && (
                    <PlayCircle className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  )}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <Clock className="w-4 h-4" />
                  <span>{module.duration_minutes} minutes</span>
                  {module.progress?.quiz_score && (
                    <span className="ml-auto text-green-600 font-medium">
                      Quiz: {module.progress.quiz_score}%
                    </span>
                  )}
                </div>

                {getStatusMessage(module) && (
                  <p className="text-sm text-gray-500 mb-4">{getStatusMessage(module)}</p>
                )}

                {module.status === 'completed' && (
                  <Link to={`/learn/app/modules/${module.slug}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      Review Module
                    </Button>
                  </Link>
                )}
                {module.status === 'available' && (
                  <Link to={`/learn/app/modules/${module.slug}`}>
                    <Button size="sm" className="w-full">
                      {module.progress?.completed ? 'Continue Module' : 'Start Module'}
                    </Button>
                  </Link>
                )}
                {module.status === 'locked' && (
                  <Button variant="ghost" size="sm" disabled className="w-full">
                    <Lock className="w-4 h-4 mr-2" />
                    Locked
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default LearnModules;
